import type { DriverState, TowerDriver, TowerShipment } from "../../../types/controlTower";
import { argentinaDateKey, ROUTE_DAY_START_HOUR } from "./towerShared";

export type DriverActivityAlert = "warning" | "critical" | null;

export function minutesSinceLastDelivery(raw: string | null | undefined, nowMs: number): number | null {
  const match = /^(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(raw || "");
  if (!match) return null;

  // El backend devuelve hora local argentina sin offset. Fijamos -03:00 para
  // que el cálculo no dependa de la zona horaria configurada en Windows.
  const timestamp = Date.parse(`${match[1]}T${match[2]}:${match[3]}:${match[4] || "00"}-03:00`);
  if (!Number.isFinite(timestamp) || timestamp > nowMs) return null;
  return Math.floor((nowMs - timestamp) / 60_000);
}

// Sin entregar: leve desde 1 h hasta 1 h 45; crítico después de 1 h 45.
export const WARNING_MINUTES = 60;
export const CRITICAL_MINUTES = 105;

export function driverActivityAlert(minutes: number | null): DriverActivityAlert {
  if (minutes === null || minutes < WARNING_MINUTES) return null;
  return minutes > CRITICAL_MINUTES ? "critical" : "warning";
}

// noDeliveries: tiene Flex en camino y ninguna entrega del recorrido de hoy.
// movedToday distingue "salió y todavía no entregó" de "hoy no registró
// ningún movimiento" (lo que tiene en camino viene de días anteriores).
export type DriverInactivityAlert =
  | { level: "critical" | "warning"; idleMinutes: number }
  | { level: "noDeliveries"; movedToday: boolean }
  | null;

// "YYYY-MM-DD HH:MM" del día `today`, desde las 6:00.
function isTodayRouteTime(stamp: string | null | undefined, today: string): boolean {
  const match = /^(\d{4}-\d{2}-\d{2}) (\d{2}):\d{2}/.exec(stamp || "");
  return Boolean(match && match[1] === today && Number(match[2]) >= ROUTE_DAY_START_HOUR);
}

// Texto y tono del estado del chofer. El estado lo calcula el backend
// (controlTower.logic.js, driverState); acá solo se nombra.
const DRIVER_STATE_DISPLAY: Record<DriverState, { label: string; tone: string }> = {
  inTransit: { label: "En reparto", tone: "inTransit" },
  pending: { label: "Pendiente de salida", tone: "pending" },
  incident: { label: "Incidencia en envío", tone: "incident" },
  review: { label: "Revisar estados", tone: "other" },
  finished: { label: "Finalizado", tone: "delivered" },
  idle: { label: "Sin envíos activos", tone: "other" },
};

export function driverStateDisplay(state: DriverState): { label: string; tone: string } {
  return DRIVER_STATE_DISPLAY[state];
}

export type AlertBellLevel = "warning" | "critical" | "muted";

// Campanita al lado del nombre: amarilla para la alerta leve, roja para la
// crítica y gris cuando no hay ninguna entrega hoy para medir la inactividad.
export function alertBellLevel(alert: DriverInactivityAlert): AlertBellLevel | null {
  if (!alert) return null;
  return alert.level === "noDeliveries" ? "muted" : alert.level;
}

type AlertRow = { kind: TowerDriver["kind"]; counts: Pick<TowerDriver["counts"], "flexInTransit"> };
type AlertOverall = {
  counts: Pick<TowerDriver["counts"], "delivered">;
  lastDelivery: string | null;
  shipments?: Partial<Pick<TowerShipment, "status" | "stateDate" | "operationalDate">>[];
};

// Única regla de alerta de inactividad: la usan la tabla y el orden «Por
// riesgo». `row` es la fila que se muestra (puede ser el recorte de una
// zona); `overall` es el chofer completo, porque una entrega en otra zona
// también cuenta como actividad. Sin ninguna entrega entre los envíos del
// día no se puede medir cuánto lleva inactivo: se avisa igual en vez de
// ocultar al chofer, y no se usa una entrega de otro día como referencia.
export function driverInactivityAlert(row: AlertRow, overall: AlertOverall, nowMs: number): DriverInactivityAlert {
  if (row.kind !== "driver" || row.counts.flexInTransit <= 0) return null;
  const today = argentinaDateKey(nowMs);
  // Una entrega de hoy sin hora registrada (aunque haya otras con hora): hubo
  // actividad, pero no se puede medir cuánto lleva inactivo. No se alerta.
  if (overall.counts.delivered > 0 && overall.lastDelivery === today) return null;
  if ((overall.shipments || []).some((s) => s.status === "delivered" && s.stateDate === today)) return null;
  if (overall.counts.delivered === 0 || !isTodayRouteTime(overall.lastDelivery, today)) {
    // Hubo movimiento hoy si algo cambió de estado en el recorrido de hoy o se colectó hoy.
    const movedToday = (overall.shipments || []).some(
      (s) =>
        s.stateDate === today || isTodayRouteTime(s.stateDate, today) || Boolean(s.operationalDate?.startsWith(today)),
    );
    return { level: "noDeliveries", movedToday };
  }
  const idleMinutes = minutesSinceLastDelivery(overall.lastDelivery, nowMs);
  const level = driverActivityAlert(idleMinutes);
  return level && idleMinutes !== null ? { level, idleMinutes } : null;
}
